import { Button } from "@tellescope/design-system";
import {
  FilterBlockEditor,
  MultiSelect,
  type MultiSelectOption,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@tellescope/ui";
import { type FreeformFieldOption, type FreeformFilterBlock } from "@tellescope/utils";
import { ListFilter } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export interface FieldsFilterPopoverProps {
  filterBlock: FreeformFilterBlock;
  fieldOptions: FreeformFieldOption[];
  nameOptions: string[];
  onFilterChange: (block: FreeformFilterBlock) => void;
}

export const FieldsFilterPopover = ({
  filterBlock,
  fieldOptions,
  nameOptions,
  onFilterChange,
}: FieldsFilterPopoverProps) => {
  const { t } = useTranslation("contacts");

  const nameSelectOptions = useMemo<MultiSelectOption[]>(
    () => nameOptions.map((name) => ({ label: name, value: name })),
    [nameOptions]
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button iconOnly variant="ghost" size="sm" color="muted" hoverLabel={t("Filter fields")}>
          <ListFilter />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-2" align="end">
        <FilterBlockEditor
          block={filterBlock}
          fieldOptions={fieldOptions}
          onChange={onFilterChange}
          onRemove={() => onFilterChange({ ...filterBlock, filters: [] })}
          renderValueInput={({ filter, onChange }) =>
            filter.field === "name" ? (
              <MultiSelect
                className="w-full bg-white text-xs font-light"
                minPopoverWidth={240}
                options={nameSelectOptions}
                value={Array.isArray(filter.value) ? filter.value : []}
                onValueChange={(value) => onChange({ ...filter, value })}
                placeholder={t("Select fields")}
                creator={{
                  render: (input) => t('Add "{{name}}"', { name: input }),
                  transform: (input) => input,
                  onCreate: () => {},
                }}
              />
            ) : undefined
          }
        />
      </PopoverContent>
    </Popover>
  );
};
